<template>
  <v-form>
    <v-container>
      <v-row>
        <v-col cols="8">
          <h1>
            Customize your cashier
            <v-tooltip
              style="z-index: 500"
              v-if="cashierVersion || bootstrapVersion"
              location="top"
            >
              <template v-slot:activator="{ props }">
                <v-icon
                  @click="alertVersion"
                  v-bind="props"
                  class="open-docs-icon"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span v-if="cashierMajorVersion"
                >Cashier
                {{ cashierMajorVersion === 'v3' ? 'V3 (React)' : 'V2 (Vue)' }}</span
              >
              <br />
              <span v-if="cashierVersion">Branch: {{ cashierVersion }}</span>
              <br />
              <span v-if="bootstrapVersion"
                >Bootstrap version: {{ bootstrapVersion }}</span
              >
            </v-tooltip>
          </h1>
          <p>Minimal/basic requirements for running the cashier</p>
        </v-col>
        <v-col cols="4">
          <v-autocomplete
            :items="getAllConfigNames"
            variant="solo-inverted"
            flat
            hide-details
            label="Search for a key"
            prepend-inner-icon="mdi-magnify"
            v-model="configSearchKey"
            @update:model-value="searchForConfig"
          />
          <v-tooltip v-if="preset === 'Default'" location="top">
            <template v-slot:activator="{ props }">
              <v-checkbox
                class="save-base-info"
                v-bind="props"
                v-model="saveBaseInfo"
                @mouseup="toggleSaveBaseInfo"
                :label="`Save MID & userId`"
              ></v-checkbox>
            </template>
            <span
              >Remember your merchantId &amp; userId. Only applies to 'Default'
              preset</span
            >
          </v-tooltip>
        </v-col>
        <v-col style="margin-top: -35px" cols="12">
          <v-divider></v-divider>
        </v-col>
        <v-col id="userId" style="position: relative" cols="12" sm="6">
          <v-text-field
            @update:model-value="(val) => updateConfigs(val, 'userId')"
            :label="getInfo('userId', 'label')"
            :model-value="configs.userId"
            variant="filled"
          ></v-text-field>
        </v-col>

        <v-col id="merchantId" style="position: relative" cols="12" sm="6">
          <v-text-field
            @update:model-value="(val) => updateConfigs(val, 'merchantId')"
            :label="getInfo('merchantId', 'label')"
            :model-value="configs.merchantId"
            variant="filled"
          ></v-text-field>
        </v-col>

        <v-col id="sessionId" style="position: relative" cols="12" sm="6">
          <v-text-field
            @update:model-value="(val) => updateConfigs(val, 'sessionId')"
            :label="getInfo('sessionId', 'label')"
            :model-value="configs.sessionId"
            variant="filled"
          ></v-text-field>
        </v-col>

        <v-col id="method" style="position: relative" cols="12" sm="6">
          <v-select
            @update:model-value="(val) => updateConfigs(val, 'method')"
            :items="['deposit', 'withdrawal']"
            variant="filled"
            v-model="configs.method"
            :label="getInfo('method', 'label')"
          ></v-select>
        </v-col>

        <v-col id="environment" style="position: relative" cols="12" sm="6">
          <v-select
            @update:model-value="(val) => updateConfigs(val, 'environment')"
            :items="['test', 'development', 'production']"
            variant="filled"
            v-model="configs.environment"
            :label="getInfo('environment', 'label')"
          ></v-select>
        </v-col>

        <v-col cols="12" sm="6">
          <v-autocomplete
            @update:model-value="newPresetChoosed"
            :items="getPresetsNames"
            variant="filled"
            v-model="presetChoosedModel"
            label="Presets"
          ></v-autocomplete>
        </v-col>

        <v-col cols="7">
          <h2>
            Advanced
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon
                  @click="openInDocs"
                  v-bind="props"
                  class="open-docs-icon"
                  >mdi-open-in-new</v-icon
                >
              </template>
              <span>Read more about each property</span>
            </v-tooltip>
          </h2>
          <span> Configure the cashier with more features. </span>
        </v-col>
        <v-col cols="5">
          <v-autocomplete
            :items="advancedSections"
            variant="solo-inverted"
            flat
            hide-details
            label="Advanced sections"
            prepend-inner-icon="mdi-magnify"
            @update:model-value="goToSection"
          />
        </v-col>
      </v-row>
      <v-divider></v-divider>
    </v-container>

    <AdvancedConfigs
      :advancedConfigs="advancedConfigs"
      :updateConfigs="updateConfigs"
    />

    <FetchConfigSection :updateConfigs="updateConfigs" />

    <AttributesSection />
  </v-form>
</template>

<script>
import { getIframeUrl } from '@/http/service'
import { ADVANCED_CATEGORIES } from '@/utils/types'
import { mapState, mapMutations, mapActions } from 'vuex'
import { initialConfig } from '@/utils/initialConfig'
import { configsInfo } from '@/utils/allConfigs'
import {
  initialTheme,
  getCashierUrlInfo,
  getBootstrapVersionFromUrl,
  parseCashierUrlInfo
} from '@/utils/helpers'
import {
  getMockCashierUrl,
  installMockCashierHelper
} from '@/utils/mockCashierUrl'
import AdvancedConfigs from './AdvancedConfigs'
import FetchConfigSection from './FetchConfigSection'
import AttributesSection from './AttributesSection'

export default {
  name: 'Footer',
  components: {
    AdvancedConfigs,
    FetchConfigSection,
    AttributesSection
  },
  data: () => ({
    msg: null,
    cashierVersionPollLimit: 0,
    configSearchKey: null,
    saveBaseInfo: localStorage.getItem('saveBaseInfo')
      ? localStorage.getItem('saveBaseInfo') === 'true'
      : true
  }),
  computed: {
    ...mapState({
      config: ({ context }) => context.config,
      preset: ({ config }) => config.preset,
      excludeConfigs: ({ context }) => context.excludeConfigs,
      cashierVersion: ({ context }) => context.cashierVersion,
      cashierMajorVersion: ({ context }) => context.cashierMajorVersion,
      bootstrapVersion: ({ context }) => context.bootstrapVersion,
      presets: ({ config }) => config.allPresets
    }),
    presetChoosedModel: {
      get() {
        return this.preset
      },
      set(preset) {
        this.setPresetName(preset)
      }
    },
    advancedSections() {
      const sections = []
      for (const i in ADVANCED_CATEGORIES) {
        sections.push(ADVANCED_CATEGORIES[i])
      }
      const extraSections = ['FetchConfig', 'Bootstrapper', 'Attributes']
      extraSections.forEach((section) => sections.push(section))
      return sections
    },
    advancedConfigs() {
      const { excludeConfigs, config: initialConfig } = this
      const configInfo = { ...this.getAllConfigs }
      // Keys added to this basicConfigs array will be excluded in advanced configs. Make sure to add all basic configs here.
      const basicConfigs = [
        'merchantId',
        'userId',
        'sessionId',
        'environment',
        'method'
      ]
      const advancedConfigs = []
      for (const C in ADVANCED_CATEGORIES) {
        const category = ADVANCED_CATEGORIES[C]
        const categoryObj = {
          category,
          configs: []
        }
        for (const config in configInfo) {
          const key = configInfo[config]
          if (!Object.prototype.hasOwnProperty.call(key, 'category')) {
            key.category = ADVANCED_CATEGORIES.OTHERS
          }
          if (
            basicConfigs.includes(config) ||
            excludeConfigs.includes(config)
          ) {
            continue
          }
          key.val = initialConfig[config]
          key.propName = config
          if (key.category === category) {
            categoryObj.configs.push(key)
          }
        }
        advancedConfigs.push(categoryObj)
      }
      return advancedConfigs
    },
    getPresets() {
      return this.presets
    },
    getAllConfigs() {
      return configsInfo()
    },
    getAllConfigNames() {
      const { getAllConfigs } = this
      const arr = []
      for (const config in getAllConfigs) {
        arr.push(config)
      }
      return arr.filter((val) => !this.excludeConfigs.includes(val))
    },
    getPresetsNames() {
      const allPresets = this.getPresets
      const presetNames = []
      for (const i in allPresets) {
        presetNames.push(i)
      }
      return presetNames.sort()
    },
    configs() {
      const { config } = this
      return {
        merchantId: config.merchantId,
        userId: config.userId,
        sessionId: config.sessionId,
        environment: config.environment,
        method: config.method
      }
    }
  },
  methods: {
    ...mapMutations([
      'setPresetName',
      'setCashierVersion',
      'setCashierBaseUrl',
      'setCashierMajorVersion',
      'setBootstrapVersion'
    ]),
    ...mapActions(['setConfigs', 'updateTheme', 'overrwriteConfigs']),
    elementBounceEffect(elmnt) {
      elmnt.className += ' config-search-highlight bounce'
      setTimeout(() => {
        elmnt.className = elmnt.className.replace(
          /config-search-highlight bounce/g,
          ''
        )
        this.configSearchKey = null
      }, 3600)
    },
    goToSection(val) {
      const elmnt = document.getElementById(val)
      elmnt.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
    toggleSaveBaseInfo() {
      localStorage.setItem('saveBaseInfo', !this.saveBaseInfo)
    },
    searchForConfig(config) {
      const elmnt = document.getElementById(config)
      elmnt.scrollIntoView({ block: 'end', behavior: 'smooth' })
      this.elementBounceEffect(elmnt)
    },
    alertVersion() {
      alert(`Branch: ${this.cashierVersion}`)
    },
    getInfo(config, type) {
      return this.getAllConfigs[config][type]
    },
    updateConfigs(newConfig, key) {
      const defaultPreset = this.preset === 'Default'
      const MIDOrUID = key === 'merchantId' || key === 'userId'
      const saveBaseInfo =
        localStorage.getItem('saveBaseInfo') === null ||
        localStorage.getItem('saveBaseInfo') !== 'false'
      if (defaultPreset && saveBaseInfo && MIDOrUID) {
        localStorage.setItem(key, newConfig)
      }
      const config = {
        [key]: newConfig
      }
      this.setConfigs(config)
      this.callNewCashierVersion(key)
    },
    callNewCashierVersion(key) {
      // The cashier version/base url is tied to the merchant config, so only
      // re-detect when the merchantId changes. Calls with no key (initial mount
      // and preset switches, which can change the merchantId) also proceed;
      // every other config edit is ignored.
      if (key !== undefined && key !== 'merchantId') { return null }
      this.setCashierVersion(null)
      this.setCashierBaseUrl(null)
      this.setCashierMajorVersion(null)
      setTimeout(() => {
        this.setNewCashierVersion()
      }, 2000)
    },
    async setNewCashierVersion() {
      // Dev-only: on localhost the cross-origin iframe can't be read, so allow a
      // mocked resolved url to drive detection. No-op in production builds.
      const mockUrl = getMockCashierUrl()
      if (mockUrl) {
        const urlInfo = parseCashierUrlInfo(mockUrl)
        if (urlInfo && urlInfo.versionPath) {
          this.setCashierVersion(urlInfo.versionPath)
          this.setCashierBaseUrl(urlInfo.baseUrl)
          this.setCashierMajorVersion(urlInfo.majorVersion)
          console.log(
            `[mock] Cashier version: ${urlInfo.versionPath} (${urlInfo.majorVersion})`
          )
        }
        return
      }
      // We can not reach iframe when on localhost due to CORS restrictions, we can only do this when deployed to https://checkout.paymentiq.io...
      const isInProduction = process.env.NODE_ENV === 'production'
      if (isInProduction) {
        const iframeUrl = document.querySelector('#cashierIframe') || document.querySelector('#cashier-preview iframe')
        let contentWindow = null
        try {
          contentWindow = iframeUrl.contentWindow.location.href
        } catch (err) {
          console.log(err)
        }
        if (iframeUrl && contentWindow && contentWindow !== 'about:blank') {
          try {
            const urlInfo = await getCashierUrlInfo(getIframeUrl)
            if (urlInfo && urlInfo.versionPath) {
              this.setCashierVersion(urlInfo.versionPath)
              this.setCashierBaseUrl(urlInfo.baseUrl)
              this.setCashierMajorVersion(urlInfo.majorVersion)
              console.log(`Cashier version: ${urlInfo.versionPath} (${urlInfo.majorVersion})`)
              this.cashierVersionPollLimit = 0
            }
          } catch (err) {
            console.log(err)
          }
        } else if (isInProduction) {
          setTimeout(() => {
            this.cashierVersionPollLimit++
            if (this.cashierVersionPollLimit < 8) {
              this.setNewCashierVersion()
            }
          }, 1500)
        }
        const bootstrapVersion = await getBootstrapVersionFromUrl(getIframeUrl)
        if (bootstrapVersion) {
          this.setBootstrapVersion(bootstrapVersion)
        }
      }
    },
    newPresetChoosed(theme) {
      this.setPresetName(theme)
      const theTheme = this.getPresets[theme]
      if (!theTheme) {
        // Field was cleared (theme = null) or an unknown preset — nothing to apply.
        return
      }
      const newTheme = {
        ...initialTheme(),
        ...theTheme.theme
      }
      this.updateTheme(newTheme)
      const newConfigs = {
        ...initialConfig(),
        ...theTheme.config
      }
      this.overrwriteConfigs(newConfigs)
      this.callNewCashierVersion()
    },
    openInDocs() {
      window.open(
        'https://docu-portal.paymentiq.io/docs/apis_and_integration/cashier/cashier_parameter_details'
      )
    }
  },
  mounted() {
    // Dev-only: expose window.setMockCashierVersion() and re-detect on change.
    installMockCashierHelper(() => this.setNewCashierVersion())
    this.callNewCashierVersion()
  },
  created() {
    this.cashierVersionPollLimit = 0
  }
}
</script>

<style lang="scss" scoped>
.open-docs-icon {
  cursor: pointer;
}

.save-base-info {
  .v-label {
    font-size: 10px !important;
  }
}
</style>
